import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPhoneAlt, FaEnvelope, FaChevronDown, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import './LandingPage.css';

const PHONE_DISPLAY = '+381 63 113 4892';
const PHONE_TEL = '+381631134892';
const EMAIL = 'info@juniorfk.com';

type Highlight = { title: string; text: string };
type Section = { title: string; paragraphs?: string[]; bullets?: string[] };
type Faq = { q: string; a: string };
type LinkItem = { label: string; href: string };

interface LandingPageProps {
  /** Ključ u prevodima, npr. 'landing.fudbalZaDecu'. */
  i18nKey: string;
}

/**
 * Namenska SEO stranica (npr. "Fudbal za decu Novi Sad", "Fudbalski klub Novi Sad").
 * Sav sadržaj dolazi iz prevoda pod zadatim ključem; FAQ se koristi i za JSON-LD u post-build koraku.
 */
const LandingPage: React.FC<LandingPageProps> = ({ i18nKey }) => {
  const { t, tList } = useLanguage();
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const k = (s: string) => `${i18nKey}.${s}`;

  const highlights = tList<Highlight>(k('highlights'));
  const sections = tList<Section>(k('sections'));
  const faq = tList<Faq>(k('faq'));
  const links = tList<LinkItem>(k('links'));

  const reveal = (delay = 0) => ({
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.5, delay },
    viewport: { once: true },
  });

  return (
    <div className="App App--cup landing-page">
      <Header />
      <main>
        <section className="landing-hero">
          <div className="container">
            <motion.p className="landing-hero__kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
              {t(k('kicker'))}
            </motion.p>
            <motion.h1 className="landing-hero__title" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
              {t(k('title'))}
            </motion.h1>
            <motion.p className="landing-hero__lead" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
              {t(k('lead'))}
            </motion.p>
            <motion.div className="landing-hero__actions" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}>
              <a className="landing-btn landing-btn--primary" href="/upis/">
                {t('landing.common.enrollBtn')} <FaArrowRight aria-hidden />
              </a>
              <a className="landing-btn" href={`tel:${PHONE_TEL}`}>
                <FaPhoneAlt aria-hidden /> <span>{PHONE_DISPLAY}</span>
              </a>
            </motion.div>
          </div>
        </section>

        {highlights.length > 0 && (
          <section className="landing-highlights">
            <div className="container">
              <ul className="landing-highlights__list">
                {highlights.map((h, i) => (
                  <motion.li key={h.title} {...reveal(i * 0.07)}>
                    <FaCheckCircle className="landing-highlights__icon" aria-hidden />
                    <strong>{h.title}</strong>
                    <span>{h.text}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </section>
        )}

        <section className="landing-content">
          <div className="container landing-content__inner">
            {sections.map((s, i) => (
              <motion.section key={s.title} className="landing-section" {...reveal(0.05)}>
                <h2 className="landing-section__title">{s.title}</h2>
                {(s.paragraphs || []).map((p) => (
                  <p key={p.slice(0, 40)} className="landing-section__text">{p}</p>
                ))}
                {s.bullets && s.bullets.length > 0 && (
                  <ul className="landing-section__list">
                    {s.bullets.map((b) => (
                      <li key={b}><FaCheckCircle aria-hidden /> <span>{b}</span></li>
                    ))}
                  </ul>
                )}
              </motion.section>
            ))}

            {faq.length > 0 && (
              <motion.section className="landing-section landing-faq" {...reveal(0.05)}>
                <h2 className="landing-section__title">{t(k('faqTitle'))}</h2>
                <ul className="landing-faq__list">
                  {faq.map((item, i) => {
                    const open = openFaq === i;
                    return (
                      <li key={item.q} className={`landing-faq__item ${open ? 'is-open' : ''}`}>
                        <button
                          type="button"
                          className="landing-faq__question"
                          aria-expanded={open}
                          aria-controls={`landing-faq-${i}`}
                          onClick={() => setOpenFaq(open ? null : i)}
                        >
                          <span>{item.q}</span>
                          <FaChevronDown className="landing-faq__chevron" aria-hidden />
                        </button>
                        <div id={`landing-faq-${i}`} className="landing-faq__answer" hidden={!open}>
                          <p>{item.a}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </motion.section>
            )}

            {links.length > 0 && (
              <motion.nav className="landing-links" aria-label={t('landing.common.moreTitle')} {...reveal(0.05)}>
                <h2 className="landing-section__title">{t('landing.common.moreTitle')}</h2>
                <ul>
                  {links.map((l) => (
                    <li key={l.href}>
                      <a href={l.href}>{l.label} <FaArrowRight aria-hidden /></a>
                    </li>
                  ))}
                </ul>
              </motion.nav>
            )}
          </div>
        </section>

        <section className="landing-cta">
          <div className="container">
            <motion.div className="landing-cta__box" {...reveal()}>
              <h2>{t(k('cta.title'))}</h2>
              <p>{t(k('cta.text'))}</p>
              <div className="landing-hero__actions">
                <a className="landing-btn landing-btn--primary" href={`tel:${PHONE_TEL}`}>
                  <FaPhoneAlt aria-hidden /> <span>{PHONE_DISPLAY}</span>
                </a>
                <a className="landing-btn" href={`mailto:${EMAIL}?subject=${encodeURIComponent(t('landing.common.emailSubject'))}`}>
                  <FaEnvelope aria-hidden /> <span>{EMAIL}</span>
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
